import { useCallback, useState, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Segmented } from '../Segmented'
import { Switch } from '../Switch'
import { Icon } from '../Icon'
import { Btn } from '../Btn'
import { useToast } from '../useOverlay'
import { useAdminAuth } from '../../hooks/useAdminAuth'
import { useOrientationPref } from '../../state/settings'
import {
  LS_THEME, LS_HAPTIC, LS_LANG,
  type Theme,
  lsGet, lsSet, lsBool, applyThemeToDom,
  fireHaptic,
} from './SettingsActions'
import { syncSettings } from '../../lib/syncSettings'
import { SUPPORT_EVM_ADDRESS } from '../../lib/supportAddress'

// ── Section header ─────────────────────────────────────────────────────────────

function SectionCap({ label }: { label: string }): ReactElement {
  return (
    <div
      className="cap"
      style={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600, marginTop: 8 }}
    >
      {label}
    </div>
  )
}

// ── Chevron row (navigation) ───────────────────────────────────────────────────

function NavRow({ label, danger, onClick }: { label: string; danger?: boolean; onClick: () => void }): ReactElement {
  return (
    <button
      type="button"
      className="set-row set-row--btn"
      onClick={onClick}
      style={{ background: 'none', border: 'none', width: '100%', padding: 0, cursor: 'pointer' }}
    >
      <div className="set-row__lbl" style={danger ? { color: 'var(--danger)' } : undefined}>{label}</div>
      <Icon name="chev" size={18} color={danger ? 'var(--danger)' : 'var(--tg-hint-color)'} />
    </button>
  )
}

// ── SettingsBody ───────────────────────────────────────────────────────────────

export function SettingsBody({ onClose }: { onClose: () => void }): ReactElement {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { showToast } = useToast()
  const { status: adminStatus } = useAdminAuth()
  const [orientPref, setOrientPref] = useOrientationPref()

  const [theme, setThemeState] = useState<Theme>(() => lsGet(LS_THEME, 'auto') as Theme)
  const [haptic, setHapticState] = useState<boolean>(() => lsBool(LS_HAPTIC, true))
  const [lang, setLangState] = useState<string>(() => lsGet(LS_LANG, i18n.language?.slice(0, 2) ?? 'ko'))
  const [copiedSupport, setCopiedSupport] = useState<boolean>(false)

  /**
   * W3W4 P2-c — syncSettings handles its own debounce + single-in-flight gate
   * + dirty-check (no PATCH if delta empty). Dirty-check at this call-site
   * compares against the previously-applied React state value; if identical,
   * skip the call entirely.
   */
  const dirtyAndSync = useCallback(
    (key: 'theme' | 'language' | 'hapticEnabled', value: string | boolean, prev: string | boolean): void => {
      if (value === prev) return
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const delta: any = { [key]: value }
      void syncSettings(delta)
    },
    [],
  )

  const applyTheme = useCallback((next: string): void => {
    const t2 = next as Theme
    const prev = theme
    setThemeState(t2)
    lsSet(LS_THEME, t2)
    applyThemeToDom(t2)
    showToast('success', t('settings.toast.saved'))
    dirtyAndSync('theme', t2, prev)
  }, [showToast, t, theme, dirtyAndSync])

  const applyHaptic = useCallback((next: boolean): void => {
    const prev = haptic
    setHapticState(next)
    lsSet(LS_HAPTIC, next ? '1' : '0')
    showToast('success', t('settings.toast.saved'))
    dirtyAndSync('hapticEnabled', next, prev)
  }, [showToast, t, haptic, dirtyAndSync])

  const applyLang = useCallback((next: string): void => {
    const prev = lang
    setLangState(next)
    lsSet(LS_LANG, next)
    void i18n.changeLanguage(next)
    showToast('success', t('settings.toast.saved'))
    dirtyAndSync('language', next, prev)
  }, [i18n, showToast, t, lang, dirtyAndSync])

  const applyOrient = useCallback((next: string): void => {
    const pref = next as 'auto' | 'landscape' | 'portrait'
    setOrientPref(pref)
    showToast('success', t('settings.toast.saved'))
    // Orientation pref is purely client-side — NOT in MePatchRequestSchema.
    // Stays in localStorage only.
    void pref
  }, [setOrientPref, showToast, t])

  const handleClearCache = useCallback((): void => {
    // W1: window.confirm as modal not yet wired inside drawer z-stack.
    // useModal (commit 5) pushes to the same overlay stack — stacking with
    // Drawer would require z-index coordination; deferred to W2.
    const confirmed = window.confirm(t('settings.row.clearCache') + ' — 정말로 캐시를 비우시겠어요?')
    if (!confirmed) return
    try { window.localStorage.clear() } catch { /* no-op */ }
    try { const req = window.indexedDB.deleteDatabase('sagwa'); req.onerror = null } catch { /* no-op */ }
    window.location.reload()
  }, [t])

  const handleTutorialReset = useCallback((): void => {
    localStorage.removeItem('dailyTutorialDismissed')
    showToast('success', t('tutorial.resetDone'))
  }, [showToast, t])

  const handleAdmin = useCallback((): void => { onClose(); navigate('/admin') }, [onClose, navigate])

  const handleDailyHistory = useCallback((): void => {
    onClose()
    navigate('/daily/history')
  }, [onClose, navigate])

  const handleCopySupport = useCallback((): void => {
    void (async () => {
      try {
        await navigator.clipboard.writeText(SUPPORT_EVM_ADDRESS)
        setCopiedSupport(true)
        fireHaptic('success')
        setTimeout(() => setCopiedSupport(false), 1500)
      } catch {
        fireHaptic('error')
      }
    })()
  }, [])

  const themeOpts = [{ value: 'auto', label: t('settings.themeOption.auto') }, { value: 'light', label: t('settings.themeOption.light') }, { value: 'dark', label: t('settings.themeOption.dark') }]
  const langOpts = [{ value: 'ko', label: '한국어' }, { value: 'en', label: 'English' }]
  const orientOpts = [{ value: 'auto', label: t('settings.orientationOption.auto') }, { value: 'landscape', label: t('settings.orientationOption.landscape') }, { value: 'portrait', label: t('settings.orientationOption.portrait') }]

  const tgUser = window.Telegram?.WebApp?.initDataUnsafe?.user
  const displayName = tgUser ? [tgUser.first_name, tgUser.last_name].filter(Boolean).join(' ') : '—'
  const buildSha = (import.meta as ImportMeta & { env?: Record<string, string> }).env?.VITE_BUILD_SHA ?? 'dev'

  return (
    <div className="settings-body">
      <SectionCap label={t('settings.section.account')} />
      <div className="set-row">
        <div>
          <div className="set-row__lbl">{t('settings.row.displayName')}</div>
          <div className="set-row__sub">{t('settings.row.displayNameHint')}</div>
        </div>
        <div style={{ color: 'var(--tg-hint-color)' }}>{displayName}</div>
      </div>

      <SectionCap label={t('settings.section.display')} />
      <div className="set-row">
        <div className="set-row__lbl">{t('settings.row.theme')}</div>
        <div style={{ width: 200 }}><Segmented value={theme} options={themeOpts} onChange={applyTheme} /></div>
      </div>
      <div className="set-row">
        <div className="set-row__lbl">{t('settings.row.language')}</div>
        <div style={{ width: 160 }}><Segmented value={lang} options={langOpts} onChange={applyLang} /></div>
      </div>
      <div className="set-row">
        <div className="set-row__lbl">{t('settings.row.orientation')}</div>
        <div style={{ width: 200 }}><Segmented value={orientPref} options={orientOpts} onChange={applyOrient} /></div>
      </div>
      <div className="set-row">
        <div className="set-row__lbl">{t('settings.row.haptic')}</div>
        <Switch on={haptic} onChange={applyHaptic} aria-label={t('settings.row.haptic')} />
      </div>
      <div className="set-row" style={{ opacity: 0.5 }}>
        <div>
          <div className="set-row__lbl">{t('settings.row.sound')}</div>
          <div className="set-row__sub">Phase 4</div>
        </div>
        <Switch on={false} />
      </div>

      <SectionCap label={t('settings.section.data')} />
      <NavRow label={t('settings.row.dailyHistory')} onClick={handleDailyHistory} />
      <button
        type="button"
        className="set-row set-row--btn"
        onClick={handleTutorialReset}
        style={{ background: 'none', border: 'none', width: '100%', padding: 0, cursor: 'pointer' }}
      >
        <div>
          <div className="set-row__lbl">{t('settings.tutorial.reset')}</div>
          <div className="set-row__sub">{t('tutorial.resetSub')}</div>
        </div>
        <Icon name="chev" size={18} color="var(--tg-hint-color)" />
      </button>
      <NavRow label={t('settings.row.clearCache')} danger onClick={handleClearCache} />

      <SectionCap label={t('settings.section.about')} />
      <div className="set-row">
        <div className="set-row__lbl">{t('settings.row.version')}</div>
        <span className="cap">v0.1.0 ({buildSha})</span>
      </div>
      <NavRow label={t('settings.row.changelog')} onClick={() => { /* W4 noop */ }} />
      <NavRow label={t('settings.row.licenses')} onClick={() => { /* W4 noop */ }} />
      <div className="set-row">
        <div className="set-row__lbl">{t('settings.row.developer')}</div>
        <span className="cap" style={{ color: 'var(--tg-link-color)' }}>@ratskra</span>
      </div>
      {adminStatus === 'authed' && <NavRow label={t('settings.row.admin')} onClick={handleAdmin} />}

      <SectionCap label={t('settings.section.support')} />
      <div className="set-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
        <div style={{ fontSize: 13, color: 'var(--text-2, var(--tg-hint-color))', lineHeight: 1.5 }}>
          {t('settings.support.intro')}
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-2, var(--tg-hint-color))', lineHeight: 1.5 }}>
          {t('settings.support.introCoffee')}
        </div>
        <div
          style={{
            background: 'var(--bg-elev-1, rgba(0,0,0,0.04))',
            border: '1px solid var(--border-subtle, rgba(0,0,0,0.08))',
            borderRadius: 8,
            padding: 12,
            marginTop: 4,
          }}
        >
          <div
            style={{
              fontSize: 11,
              color: 'var(--text-3, var(--tg-hint-color))',
              marginBottom: 6,
              textTransform: 'uppercase',
              letterSpacing: 0.4,
            }}
          >
            {t('settings.support.addressLabel')}
          </div>
          <code
            style={{
              display: 'block',
              fontFamily: 'var(--font-mono, ui-monospace, SFMono-Regular, monospace)',
              fontSize: 12,
              wordBreak: 'break-all',
              lineHeight: 1.5,
              marginBottom: 8,
            }}
          >
            {SUPPORT_EVM_ADDRESS}
          </code>
          <Btn variant="secondary" sm block onClick={handleCopySupport}>
            {copiedSupport ? t('settings.support.copied') : t('settings.support.copyButton')}
          </Btn>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-2, var(--tg-hint-color))', lineHeight: 1.7 }}>
          <div>{t('settings.support.chainHint')}</div>
          <div>{t('settings.support.tokenHint')}</div>
        </div>
        <div style={{ fontSize: 13, marginTop: 4, color: 'var(--text-2, var(--tg-hint-color))' }}>
          {t('settings.support.thanks')}
        </div>
      </div>
    </div>
  )
}
